import { inject, Injectable } from '@angular/core';
import { map } from 'rxjs';
import { ConstructAPI } from 'src/app/API/constructAPI';
import { RestApiService } from 'src/app/API/restapi';
import { EInvoiceDoc, IAPIEInvoice } from 'src/model/e-invoice..model';
import { Download, FileService } from './file.service';

interface EInvoiceDocOptions {
  businessId?: number,
  licenseId?: number,
  issuedAfter?: Date,
  issuedBefore?: Date,
  submittedAfter?: Date,
  submittedBefore?: Date,
  submitted?: boolean
}

@Injectable({
  providedIn: 'root'
})
class EInvoiceAdapter {
  adapt(item: any): EInvoiceDoc {
    return new EInvoiceDoc(
      item.id,
      item.longId,
      item.number,
      item.businessLicenseId,
      item.businessName,
      item.totalInterest,
      new Date(item.issuanceDate),
      item.formJIds,
      new Date(item.createdOn),
      item.remark,
      item.submissionUuid,
      item.submittedOn && new Date(item.submittedOn),
      item.submittedBy,
      item.validationUrl
    )
  }

  toApiModel(eInvoiceDoc: EInvoiceDoc): IAPIEInvoice {
    return {
      issuanceDate: eInvoiceDoc.IssuanceDate.toISOString(),
      number: eInvoiceDoc.EInvoiceNumber,
      ...(eInvoiceDoc.FormJIds && { formJIds: eInvoiceDoc.FormJIds }),
      remark: eInvoiceDoc.Remark
    } as IAPIEInvoice;
  }
}

@Injectable({
  providedIn: 'root'
})
export class EInvoiceService {
  private api = inject(RestApiService);
  private constructApi = inject(ConstructAPI);
  private adapter = inject(EInvoiceAdapter);
  private fileService = inject(FileService);

  getAllEInvoiceDocs(o?: EInvoiceDocOptions): Promise<EInvoiceDoc[]> {
    return new Promise<EInvoiceDoc[]>((resolve, reject) => {
      this.api.get<EInvoiceDoc[]>(this.constructApi.getAllEInvoiceDocs(o?.businessId, o?.licenseId, o?.issuedAfter, o?.issuedBefore, o?.submittedAfter, o?.submittedBefore, o?.submitted)).pipe(
        map(data => data.map(d => this.adapter.adapt(d)))
      ).subscribe({
        next: (data) => resolve(data),
        error: (error) => {
          this.api.handleError(error);
          reject(error);
        }
      });
    });
  }

  postEInvoiceDocs(eInvoiceDoc: EInvoiceDoc) {
    return new Promise<EInvoiceDoc>((resolve, reject) => {
      this.api.post<EInvoiceDoc>(this.constructApi.getAllEInvoiceDocs(), this.adapter.toApiModel(eInvoiceDoc)).subscribe({
        next: (data) => resolve(data),
        error: (error) => {
          this.api.handleError(error);
          reject(error);
        }
      });
    });
  }

  putEInvoiceDocs(eInvoiceDoc: EInvoiceDoc) {
    return new Promise<EInvoiceDoc>((resolve, reject) => {
      this.api.put<EInvoiceDoc>(this.constructApi.getEInvoiceDocById(eInvoiceDoc.Id), this.adapter.toApiModel(eInvoiceDoc)).subscribe({
        next: (data) => resolve(data),
        error: (error) => {
          this.api.handleError(error);
          reject(error);
        }
      });
    });
  }

  deleteEInvoiceDocs(ids: number[]) {
    const data = { documentIds: ids };
    return new Promise<void>((resolve, reject) => {
      this.api.delete(this.constructApi.getAllEInvoiceDocs(), data).subscribe({
        next: () => resolve(),
        error: (error) => {
          this.api.handleError(error);
          reject(error);
        }
      });
    });
  }

  postEInvoiceExcelFile(docIds: number[]) {
    const data = { documentIds: docIds };
    return new Promise<Download>((resolve, reject) => {
      this.api.postToGetFile(this.constructApi.getEInvoiceExcel(), data).subscribe({
        next: response => {
          resolve(this.fileService.createFile(response));
        },
        error: err => {
          this.api.handleError(err);
          reject(err);
        }
      })
    })
  }
}
